import { Link } from 'react-router-dom';
import { useT } from '@dia/i18n';
import { loadShell } from '@dia/content';
import { LangToggle, Screen } from '@dia/ui';
import { useAuth } from '../auth';
import { delay, MotionStyles, useIntro } from './parts/motion';
import { PhotoPanel } from './parts/PhotoPanel';
import { DocArticle, Prose } from './parts/Prose';

/**
 * Arrival information is the most opened section of any guest guide, so it is
 * the front door, and it is public: it works before the access window opens
 * and with the network off, because a guest is told to open it once at home.
 *
 * It opens on the island from the water, a full bleed panel, and its pieces
 * arrive in order the first time it is opened in a visit. The words are
 * Markdown files in content/guest/arrive/, so a writer edits them without
 * touching this file. Pages with a `step` in their front matter are the real
 * sequence (flight, dock, water, first hour) and are the only numbered thing
 * in the app.
 */
const content = loadShell('guest');

export function Arrive() {
  const { t, lang } = useT();
  const { session } = useAuth();
  const intro = useIntro();

  const pages = content.section('arrive');
  const steps = pages.filter((p) => typeof p.meta.step === 'number');
  const extras = pages.filter((p) => typeof p.meta.step !== 'number');

  return (
    <div className={intro ? 'dia-intro' : undefined}>
      <MotionStyles />
      <Screen bleed>
        {/*
          overflow-hidden is defect D10, not decoration. The opening moment
          settles the panel from scale(1.07) to scale(1), and a 390 px viewport
          scaled by 1.07 is 417 px, so the page could be scrolled sideways by
          about ten pixels while the animation ran. Lighthouse caught it as
          content-width and marked the PWA score down for it. Clipping the hero
          keeps the movement and takes the sideways scroll away.
        */}
        <section className="relative -mx-4 h-[74dvh] min-h-[26rem] overflow-hidden">
          <div className="dia-settle absolute inset-0">
            <PhotoPanel
              src="/photos/island-from-water.webp"
              alt=""
              todo="the island from the water, early morning, boat in the foreground"
            />
          </div>

          {/*
            Arrive is reached two ways: as the front door before a guest ever
            signs in, and now also as a tile from the Home screen. The second
            path had no way back out (2026-09-26 bug report): every other
            screen gets one from BackLink, but Arrive predates the tile home
            and was never given one when it stopped being the entry point.
          */}
          <div
            className="dia-fade absolute left-3 top-[max(0.75rem,env(safe-area-inset-top))]"
            style={delay(1100)}
          >
            <Link
              to="/"
              className="inline-flex min-h-tap items-center rounded bg-ground/70 px-3 text-ink underline underline-offset-4"
            >
              {'‹'} {t('guest.nav.stay')}
            </Link>
          </div>

          <div
            className="dia-fade absolute right-3 top-[max(0.75rem,env(safe-area-inset-top))]"
            style={delay(1100)}
          >
            <LangToggle />
          </div>

          <div className="absolute inset-x-0 bottom-0 bg-ground/90 px-4 pt-5 pb-6">
            <div className="max-w-reading mx-auto">
              <h1 className="dia-rise font-display text-[2.1em] leading-[1.1] text-ink" style={delay(700)}>
                {t('guest.arrive.title')}
              </h1>
              <p className="dia-rise mt-3 text-inkMuted" style={delay(950)}>
                {t('guest.arrive.lede')}
              </p>
            </div>
          </div>
        </section>

        <div className="max-w-reading w-full mx-auto pt-8 pb-10 flex flex-col gap-10">
          <p className="text-inkFaint text-caption">{t('guest.home.offline_tip')}</p>

          {steps.length > 0 && (
            <section aria-labelledby="way-in" className="flex flex-col gap-8">
              <h2 id="way-in" className="font-display text-[1.25em] text-ink">
                {t('guest.arrive.steps_intro')}
              </h2>
              <ol className="flex flex-col gap-10">
                {steps.map((page, i) => (
                  <li key={page.path} className="dia-rise flex gap-4" style={delay(1250 + i * 140)}>
                    <span
                      aria-hidden="true"
                      className="font-display text-[2.2em] leading-none text-mark w-8 shrink-0"
                    >
                      {String(page.meta.step)}
                    </span>
                    <div className="min-w-0">
                      <p className="sr-only">{t('guest.arrive.step', { n: String(page.meta.step) })}</p>
                      <h3 className="font-display text-[1.4em] leading-snug text-ink">{page.title[lang]}</h3>
                      {page.lede && <p className="text-inkFaint text-caption mt-1 mb-3">{page.lede[lang]}</p>}
                      <Prose doc={page} />
                    </div>
                  </li>
                ))}
              </ol>
            </section>
          )}

          {extras.length > 0 && (
            <section aria-labelledby="good-to-know" className="flex flex-col gap-8 border-t border-line pt-8">
              <h2 id="good-to-know" className="font-display text-[1.25em] text-ink">
                {t('guest.arrive.good_to_know')}
              </h2>
              {extras.map((page) => (
                <DocArticle key={page.path} doc={page} level={3} />
              ))}
            </section>
          )}

          <div className="border-t border-line pt-8">
            <Link
              to={session ? '/' : '/entrar'}
              className="inline-flex items-center justify-center min-h-tap rounded bg-accent text-onAccent font-medium px-5 py-3"
            >
              {session ? t('guest.arrive.open_stay') : t('guest.arrive.sign_in')}
            </Link>
          </div>
        </div>
      </Screen>
    </div>
  );
}
